import { api } from "@/lib/api";
import type {
  CompanyPoolCreditResponse,
  CompanyUserCreditsListResponse,
  CreditAllocationRequest,
  CreditLedgerEntryResponse,
  CreditPoolTransferResponse,
  CreditReclaimRequest,
  CreditUsageAnalyticsResponse,
  CreditUsageSummaryResponse,
  PricingCatalogItem,
  UserCreditAdjustmentRequest,
  UserCreditAccountResponse,
} from "@/types/credits";
import {
  CompanyRole,
  CompanyRoleLimitSummary,
  CompanyUserListResponse,
  CompanyUserLookupResponse,
  CompanyLimit,
  CompanyUsageSummary,
  MemberCompanyInfo,
} from "@/types/company";
import {
  PublicCompanySignupRequest,
  PublicCompanySignupResponse,
} from "@/types/signup";
import type {
  CompanySettings,
  CompanySettingsUpdateRequest,
} from "@/types/profileTaxonomy";

export interface CompanyUserCreateRequest {
  email: string;
  password: string;
  full_name: string;
  role_id?: string | null;
  is_active?: boolean;
  analysis_style?: string;
  /** Omit both to inherit the company defaults. */
  industry?: string | null;
  department?: string | null;
}

export interface CompanyUserUpdateRequest {
  full_name?: string;
  role_id?: string | null;
  is_active?: boolean;
  analysis_style?: string;
  industry?: string | null;
  department?: string | null;
}

export interface CompanyRoleCreateRequest {
  name: string;
  description?: string | null;
  is_company_admin?: boolean;
  is_default?: boolean;
}

export interface CompanyRoleUpdateRequest {
  name?: string;
  description?: string | null;
  is_company_admin?: boolean;
  is_default?: boolean;
}

export interface CompanyRoleLimitUpdateRequest {
  audio_seconds_limit?: number | null;
  audio_limit_period?: string;
  agent_messages_per_week?: number | null;
  documents_limit?: number | null;
  documents_limit_period?: string;
}

export interface CompanySSODomainsResponse {
  domains: string[];
}

export interface CompanySSODomainsUpdateRequest {
  domains: string[];
}

export const companyService = {
  signupCompany: async (payload: PublicCompanySignupRequest) => {
    const response = await api.post<PublicCompanySignupResponse>(
      "/api/v1/public/companies",
      payload
    );
    return response.data;
  },

  getMyCompany: async (): Promise<MemberCompanyInfo> => {
    const response = await api.get<MemberCompanyInfo>("/api/v1/me/company");
    return response.data;
  },

  listRoles: async () => {
    const response = await api.get<CompanyRole[]>("/api/v1/company/roles");
    return response.data;
  },

  listRoleLimits: async () => {
    const response = await api.get<CompanyRoleLimitSummary[]>("/api/v1/company/roles/limits");
    return response.data;
  },

  createRole: async (payload: CompanyRoleCreateRequest) => {
    const response = await api.post<CompanyRole>("/api/v1/company/roles", payload);
    return response.data;
  },

  updateRole: async (roleId: string, payload: CompanyRoleUpdateRequest) => {
    const response = await api.put<CompanyRole>(`/api/v1/company/roles/${roleId}`, payload);
    return response.data;
  },

  deleteRole: async (roleId: string) => {
    await api.delete(`/api/v1/company/roles/${roleId}`);
  },

  listUsers: async (params?: { page?: number; page_size?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", params.page.toString());
    if (params?.page_size) query.append("page_size", params.page_size.toString());
    if (params?.search) query.append("search", params.search);
    const response = await api.get<CompanyUserListResponse>(`/api/v1/company/users?${query.toString()}`);
    return response.data;
  },

  lookupUsers: async (params?: { page?: number; page_size?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append("page", params.page.toString());
    if (params?.page_size) query.append("page_size", params.page_size.toString());
    if (params?.search) query.append("search", params.search);
    const response = await api.get<CompanyUserLookupResponse>(`/api/v1/company/users/lookup?${query.toString()}`);
    return response.data;
  },

  createUser: async (payload: CompanyUserCreateRequest) => {
    const response = await api.post("/api/v1/company/users", payload);
    return response.data;
  },

  updateUser: async (userId: string, payload: CompanyUserUpdateRequest) => {
    const response = await api.put(`/api/v1/company/users/${userId}`, payload);
    return response.data;
  },

  resetUserPassword: async (userId: string, newPassword: string) => {
    const response = await api.post(`/api/v1/company/users/${userId}/reset-password`, {
      new_password: newPassword,
    });
    return response.data;
  },

  deactivateUser: async (userId: string) => {
    await api.delete(`/api/v1/company/users/${userId}`);
  },

  getCompanyLimits: async () => {
    const response = await api.get<CompanyLimit>("/api/v1/company/limits");
    return response.data;
  },

  updateRoleLimits: async (roleId: string, payload: CompanyRoleLimitUpdateRequest) => {
    const response = await api.put(`/api/v1/company/roles/${roleId}/limits`, payload);
    return response.data;
  },

  getCompanyUsage: async () => {
    const response = await api.get<CompanyUsageSummary>("/api/v1/company/usage");
    return response.data;
  },

  getCompanyCredits: async () => {
    const response = await api.get<CompanyPoolCreditResponse>("/api/v1/company/credits");
    return response.data;
  },

  getCreditLedger: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get<CreditLedgerEntryResponse[]>(
      "/api/v1/company/credits/ledger",
      { params }
    );
    return response.data;
  },

  getCreditUsageSummary: async (params?: { start_date?: string; end_date?: string }) => {
    const response = await api.get<CreditUsageSummaryResponse>(
      "/api/v1/company/credits/usage-summary",
      { params }
    );
    return response.data;
  },

  getPricingCatalog: async () => {
    const response = await api.get<PricingCatalogItem[]>(
      "/api/v1/company/credits/pricing-catalog"
    );
    return response.data;
  },





  getPoolCredits: async (): Promise<CompanyPoolCreditResponse> => {
    const response = await api.get<CompanyPoolCreditResponse>(
      "/api/v1/company/credits",
    );
    return response.data;
  },

  listUserCredits: async (params?: {
    period_start?: string;
    period_end?: string;
  }): Promise<CompanyUserCreditsListResponse> => {
    const response = await api.get<CompanyUserCreditsListResponse>(
      "/api/v1/company/credits/users",
      { params },
    );
    return response.data;
  },

  getUsageAnalytics: async (params: {
    start_date: string;
    end_date: string;
    user_id?: string;
    role_id?: string;
    pricing_rule_id?: string;
    group_by?: string;
    chart_meter?: string;
  }): Promise<CreditUsageAnalyticsResponse> => {
    const response = await api.get<CreditUsageAnalyticsResponse>(
      "/api/v1/company/credits/usage-analytics",
      { params },
    );
    return response.data;
  },

  allocateCredits: async (
    payload: CreditAllocationRequest,
  ): Promise<CreditPoolTransferResponse> => {
    const response = await api.post<CreditPoolTransferResponse>(
      "/api/v1/company/credits/allocations",
      payload,
      { headers: { "Idempotency-Key": payload.idempotency_key } },
    );
    return response.data;
  },

  reclaimCredits: async (
    payload: CreditReclaimRequest,
  ): Promise<CreditPoolTransferResponse> => {
    const response = await api.post<CreditPoolTransferResponse>(
      "/api/v1/company/credits/reclaims",
      payload,
      { headers: { "Idempotency-Key": payload.idempotency_key } },
    );
    return response.data;
  },

  adjustUserCredits: async (
    userId: string,
    payload: UserCreditAdjustmentRequest,
  ): Promise<UserCreditAccountResponse> => {
    const response = await api.post<UserCreditAccountResponse>(
      `/api/v1/company/credits/users/${userId}/adjustments`,
      payload,
    );
    return response.data;
  },

  getSsoDomains: async () => {
    const response = await api.get<CompanySSODomainsResponse>("/api/v1/company/sso/domains");
    return response.data;
  },

  updateSsoDomains: async (payload: CompanySSODomainsUpdateRequest) => {
    const response = await api.put<CompanySSODomainsResponse>("/api/v1/company/sso/domains", payload);
    return response.data;
  },

  getSettings: async () => {
    const response = await api.get<CompanySettings>("/api/v1/company/settings");
    return response.data;
  },

  updateSettings: async (payload: CompanySettingsUpdateRequest) => {
    const response = await api.put<CompanySettings>("/api/v1/company/settings", payload);
    return response.data;
  },
};
